import React from 'react';
import { DefaultDateTimeFormatter } from '../../lib';
import { D3Tooltip } from './D3Tooltip';
import type { DoubleLineData, SingleLineData } from './LineChartUtil';
import {
  getValueWithUnit,
  isDoubleLineData,
  margin,
  getRangeIndicator
} from './LineChartUtil';

type CommonLineChartTooltipProps = {
  data: SingleLineData | DoubleLineData | null;
  visible: boolean;
  position: {
    x: number;
    y: number;
  };
  firstLineLegend?: string;
  secondLineLegend?: string;
  constrainX?: number;
};

export type LineChartTooltipProps =
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines: true;
      height: number;
      width: number;
    })
  | (CommonLineChartTooltipProps & {
      showHoverGuidelines?: false;
      height?: never;
      width?: never;
    });

export function LineChartTooltip({
  data,
  visible,
  position,
  firstLineLegend,
  secondLineLegend,
  constrainX,
  showHoverGuidelines,
  width,
  height
}: Readonly<LineChartTooltipProps>): React.JSX.Element {
  const firstLineValue = getValueWithUnit(
    data?.y.firstLine.value,
    data?.y.firstLine.unit
  );
  const isDoubleLine = isDoubleLineData(data);
  const secondLineValue =
    isDoubleLine &&
    getValueWithUnit(data.y.secondLine.value, data.y.secondLine.unit);

  const formattedDate = DefaultDateTimeFormatter.formatDate(data?.x ?? '');

  return (
    <>
      {visible && showHoverGuidelines && (
        <>
          <line
            className="hover-dashed-line"
            x1={margin.left}
            x2={width + margin.left}
            y1={position.y}
            y2={position.y}
          />
          <line
            className="hover-dashed-line"
            x1={position.x}
            x2={position.x}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        </>
      )}
      <D3Tooltip
        visible={visible}
        position={position}
        className="line-chart-tooltip"
        width={
          // determine width depending on the data
          (isDoubleLine ? 70 : 50) +
          (data?.y.firstLine.rangeIndicator ? 12 : 0) +
          Math.max(
            formattedDate.length,
            data?.y.firstLine.optionalText?.length ?? 0,
            ((isDoubleLine &&
              data.y.secondLine.optionalText?.length) as number) ?? 0,
            // to represent the padding between elements, we're adding 5 to the length
            ((firstLineLegend ?? '') + firstLineValue).length + 5,
            isDoubleLine
              ? ((secondLineLegend ?? '') + secondLineValue).length + 5
              : 0
          ) *
            6
        }
        height={isDoubleLine ? 122 : 96}
        constrainX={constrainX}
      >
        {data && (
          <>
            <tspan x="4" y="4" textAnchor="start">
              {firstLineLegend && (
                <tspan>
                  <tspan className="graph-tooltip-colored-line-secondary">
                    ━
                  </tspan>
                  <tspan dx={10} className="graph-tooltip-value-description">
                    {firstLineLegend}
                    {': '}
                  </tspan>
                </tspan>
              )}
              <tspan
                dx={firstLineLegend ? 20 : 0}
                className="graph-tooltip-value"
              >
                {firstLineValue}
              </tspan>
              {data.y.firstLine.rangeIndicator &&
                getRangeIndicator(data.y.firstLine.rangeIndicator)}
            </tspan>

            {isDoubleLine && (
              <tspan x="4" dy="28" textAnchor="start">
                <tspan>
                  <tspan className="graph-tooltip-colored-line-primary">
                    ━
                  </tspan>
                  <tspan dx={10} className="graph-tooltip-value-description">
                    {secondLineLegend}
                    {': '}
                  </tspan>
                </tspan>
                <tspan dx={20} className="graph-tooltip-value">
                  {secondLineValue}
                </tspan>
              </tspan>
            )}

            <tspan x="4" dy="28" textAnchor="start">
              {formattedDate}
            </tspan>
            <tspan x="4" dy="28" textAnchor="start">
              {isDoubleLine
                ? data.y.firstLine.optionalText ||
                  data.y.secondLine.optionalText
                : data.y.firstLine.optionalText}
            </tspan>
          </>
        )}
      </D3Tooltip>
    </>
  );
}
